import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createOtpCooldowns, cloneOtpCooldowns, OtpChannel, OtpCooldowns } from '@/shared/lib/otp/cooldowns';

interface RegistrationStateData {
  phoneNumber: string;
  nickname: string;
}

interface RegistrationSession {
  registrationToken: string | null;
  maxDeepLink: string | null;
  sendChannel: 'max' | 'sms' | null;
  cooldowns: OtpCooldowns;
  remainingAttempts: number | null;
  lockedUntil: number | null;
  lockoutMessage: string | null;
}

interface RegistrationState {
  data: RegistrationStateData;
  registrationToken: string | null;
  maxDeepLink: string | null;
  sendChannel: 'max' | 'sms' | null;
  cooldowns: OtpCooldowns;
  lastResendChannel: 'max' | 'sms' | null;
  remainingAttempts: number | null;
  lockedUntil: number | null;
  lockoutMessage: string | null;
  sessions: Record<string, RegistrationSession>;
}

const initialState: RegistrationState = {
  data: {
    phoneNumber: '',
    nickname: '',
  },
  registrationToken: null,
  maxDeepLink: null,
  sendChannel: null,
  cooldowns: createOtpCooldowns(),
  lastResendChannel: null,
  remainingAttempts: null,
  lockedUntil: null,
  lockoutMessage: null,
  sessions: {},
};

interface ResendCooldownPayload {
  channel: OtpChannel;
  timestamp: number;
  seconds: number;
}

export const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    setRegistrationData: (state, action: PayloadAction<Partial<RegistrationStateData>>) => {
      state.data = { ...state.data, ...action.payload };
    },
    resetRegistrationData: () => initialState,
    setRegistrationToken: (state, action: PayloadAction<string>) => {
      state.registrationToken = action.payload;
    },
    setMaxDeepLink: (state, action: PayloadAction<string | null>) => {
      state.maxDeepLink = action.payload;
    },
    setSendChannel: (state, action: PayloadAction<'max' | 'sms'>) => {
      state.sendChannel = action.payload;
    },
    setResendCooldown: (state, action: PayloadAction<ResendCooldownPayload>) => {
      state.cooldowns[action.payload.channel] = {
        timestamp: action.payload.timestamp,
        seconds: action.payload.seconds,
      };
    },
    setLastResendChannel: (state, action: PayloadAction<'max' | 'sms'>) => {
      state.lastResendChannel = action.payload;
    },
    setRemainingAttempts: (state, action: PayloadAction<number | null>) => {
      state.remainingAttempts = action.payload;
    },
    setLockedUntil: (state, action: PayloadAction<number | null>) => {
      state.lockedUntil = action.payload;
    },
    setLockoutMessage: (state, action: PayloadAction<string | null>) => {
      state.lockoutMessage = action.payload;
    },
    saveRegistrationSession: (state, action: PayloadAction<string>) => {
      state.sessions[action.payload] = {
        registrationToken: state.registrationToken,
        maxDeepLink: state.maxDeepLink,
        sendChannel: state.sendChannel,
        cooldowns: cloneOtpCooldowns(state.cooldowns),
        remainingAttempts: state.remainingAttempts,
        lockedUntil: state.lockedUntil,
        lockoutMessage: state.lockoutMessage,
      };
    },
    restoreRegistrationSession: (state, action: PayloadAction<string>) => {
      const session = state.sessions[action.payload];
      if (!session) return;
      state.registrationToken = session.registrationToken;
      state.maxDeepLink = session.maxDeepLink;
      state.sendChannel = session.sendChannel;
      state.cooldowns = cloneOtpCooldowns(session.cooldowns);
      state.remainingAttempts = session.remainingAttempts;
      state.lockedUntil = session.lockedUntil;
      state.lockoutMessage = session.lockoutMessage;
    },
    resetRegistrationCooldowns: (state) => {
      state.cooldowns = createOtpCooldowns();
    },
  },
});

export const {
  setRegistrationData,
  setRegistrationToken,
  setMaxDeepLink,
  setResendCooldown,
  setLastResendChannel,
  setSendChannel,
  setRemainingAttempts,
  setLockedUntil,
  setLockoutMessage,
  resetRegistrationData,
  resetRegistrationCooldowns,
  saveRegistrationSession,
  restoreRegistrationSession,
} = registrationSlice.actions;

export const { reducer: registrationReducer } = registrationSlice;
